import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  ColumnFilterOption,
  TableContentTransformations,
} from 'components/ReorderResizeTable/types';
import RoleTag from 'router/subrouters/DataRoom/subrouters/components/RoleTag';
import { COLOR_VARIANT } from 'models/Colors';
import {
  WAITING_ROOM_REQUEST_TYPE,
  WaitingRoomRequest,
  getIsBuySideInviteRequest,
} from 'models/WaitingRoom';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import LinkIcon from 'icons/Link.icon';
import utils, { getArrayLength, getUserFullName, normalizeEmail } from 'utils';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import {
  CUSTOM_SIZES,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  WAITING_ROOM_COLUMNS,
  WAITING_ROOM_PAGE_PARAM,
  WaitingRoomRow,
  WaitingRoomTableColumn,
} from './data';
import GroupPill from 'router/subrouters/DataRoom/subrouters/components/GroupPill';

import './WaitingRoomTable.styles.scss';

type WaitingRoomTableProps = {
  className?: string;
  requests: WaitingRoomRequest[];
  sort: string;
  totalPages: number;
  totalItems?: number;
  isLoading?: boolean;
  updateSort: (value: string) => void;
  onReview: (request: WaitingRoomRequest) => void;
  columnFilterOptions?: Partial<
    Record<WaitingRoomTableColumn, ColumnFilterOption>
  >;
};

const getRequestRowId = ({ _id }: WaitingRoomRow) => _id;

// The three secure link variants share one pill: what separates a password
// request from an access request, or a live link from an expired one, only
// matters once you open the request.
const TYPE_LABELS: Record<string, string> = {
  [WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_PASSWORD]: 'secureLinkType',
  [WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_ACCESS]: 'secureLinkType',
  [WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_EXPIRED]: 'secureLinkType',
  [WAITING_ROOM_REQUEST_TYPE.BUY_SIDE_INVITE]: 'buySideType',
};

const WaitingRoomTable: React.FC<WaitingRoomTableProps> = (props) => {
  const {
    className,
    requests,
    sort,
    totalPages,
    totalItems,
    isLoading = false,
    updateSort,
    onReview,
    columnFilterOptions,
  } = props;

  const { t } = useTranslation();

  const { buySideGroups } = useContext(MembersContext);

  const classes = classNames('waiting-room-table', className);

  const getRequestorName = useCallback(
    ({ firstName, lastName }: WaitingRoomRequest) =>
      getUserFullName(firstName, lastName),
    [],
  );

  const getPastTime = useCallback(
    (date: Date) => {
      const { type, count } = utils.humanize(date);

      return t(type, { count });
    },
    [t],
  );

  const renderRequestor = useCallback(
    (request: WaitingRoomRow) => {
      const name = getRequestorName(request);

      // People who request access through a secure link often have no account
      // yet, so there is no name to show until they are approved.
      return (
        <span className={classNames('requestor-cell', { unknown: !name })}>
          {name || t('unknownRequestor')}
        </span>
      );
    },
    [getRequestorName, t],
  );

  const renderType = useCallback(
    (request: WaitingRoomRow) => {
      const isBuySide = getIsBuySideInviteRequest(request);

      return (
        <RoleTag
          className="type-tag"
          variant={isBuySide ? COLOR_VARIANT.BLUE : COLOR_VARIANT.ORANGE}
        >
          {isBuySide ? <GroupUsersIcon /> : <LinkIcon />}
          {t(TYPE_LABELS[request.type] ?? request.type)}
        </RoleTag>
      );
    },
    [t],
  );

  const renderRequestingAccessTo = useCallback(
    (request: WaitingRoomRow) => {
      const { requestingAccessTo, dataRoomBuySideGroupId } = request;

      if (getIsBuySideInviteRequest(request)) {
        const group = buySideGroups.find(
          ({ _id }) => _id === dataRoomBuySideGroupId,
        );

        if (group)
          return (
            <GroupPill
              name={group.name}
              count={getArrayLength(group.dataRoomMembers)}
            />
          );
      }

      return <span className="target-cell">{requestingAccessTo}</span>;
    },
    [buySideGroups],
  );

  const renderRequested = useCallback(
    ({ requestedAt }: WaitingRoomRow) => {
      if (!requestedAt) return null;

      return <span className="requested-cell">{getPastTime(requestedAt)}</span>;
    },
    [getPastTime],
  );

  const tableContentTransformations = useMemo<
    TableContentTransformations<WaitingRoomRow>
  >(
    () => ({
      requestor: renderRequestor,
      email: ({ email }: WaitingRoomRow) => (
        <span className="email-cell">{normalizeEmail(email)}</span>
      ),
      type: renderType,
      requestingAccessTo: renderRequestingAccessTo,
      requested: renderRequested,
      actions: (request: WaitingRoomRow) => (
        <Button
          style={{ padding: '4px 8px' }}
          variant="outline"
          onClick={() => onReview(request)}
        >
          {t('review')}
        </Button>
      ),
    }),
    [
      onReview,
      renderRequested,
      renderRequestingAccessTo,
      renderRequestor,
      renderType,
      t,
    ],
  );

  return (
    <ReorderResizeTable<WaitingRoomRow>
      className={classes}
      // The queue is a fixed list of columns, not something to rearrange.
      isDragDisabled
      isResizeDisabled
      columns={WAITING_ROOM_COLUMNS}
      data={requests as WaitingRoomRow[]}
      getRowId={getRequestRowId}
      customSizes={CUSTOM_SIZES}
      headingTranslations={HEADING_TRANSLATIONS}
      sortingValues={SORTING_VALUES}
      columnFilterOptions={columnFilterOptions}
      sort={sort}
      updateSort={updateSort}
      isDataLoading={isLoading}
      totalPages={totalPages}
      totalItems={totalItems}
      pageParam={WAITING_ROOM_PAGE_PARAM}
      tableContentTransformations={tableContentTransformations}
      onRowClick={onReview}
    />
  );
};

export default WaitingRoomTable;
